import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { mealPlan } from '../RTK/defaultValues';
import { setMealPlanHint } from '../RTK/mealPlan';
import { setMealPlanHintEnabled } from '../RTK/utility';
import { selectMealPlanHintEnabled } from '../RTK/utility/selectors';

type HintKey = keyof typeof mealPlan.hint;

const nonPrefixIsShown = ['showFloatingButtonHint']; // refer to mealPlan.hint for list the does not have isShown prefix
const useMealPlanHint = () => {
  const dispatch = useDispatch();
  const isMealPlanHintEnabled = useSelector(selectMealPlanHintEnabled);

  const _maybeShowHint = useCallback(
    (hintKey: HintKey) => {
      if (isMealPlanHintEnabled) {
        const payload = { [hintKey]: true }; // action state e.g: showFeatured
        if (!nonPrefixIsShown.includes(hintKey)) {
          payload[hintKey.replace('show', 'isShown')] = true; // past tense state e.g: isShownFeatured
        }
        dispatch(setMealPlanHint(payload));
      }
    },
    [isMealPlanHintEnabled, dispatch],
  );

  const _disableMealPlanHint = () => dispatch(setMealPlanHintEnabled(false));

  const _maybeShowMealPlanFeaturedHint = useCallback(
    () => _maybeShowHint('showFeatured'),
    [_maybeShowHint],
  );
  const _maybeShowMealPlanExclusiveHint = useCallback(
    () => _maybeShowHint('showExclusive'),
    [_maybeShowHint],
  );
  const _maybeShowMealPlanNextDateHint = useCallback(
    () => _maybeShowHint('showNext'),
    [_maybeShowHint],
  );
  const _maybeShowMealPlanPrevDateHint = useCallback(
    () => _maybeShowHint('showPrev'),
    [_maybeShowHint],
  );
  const _maybeShowMealPlanCreateHint = useCallback(
    () => _maybeShowHint('showCreate'),
    [_maybeShowHint],
  );
  const _maybeShowMealPlanReviewHint = useCallback(
    () => _maybeShowHint('showReview'),
    [_maybeShowHint],
  );

  return {
    isMealPlanHintEnabled,
    disableMealPlanHint: _disableMealPlanHint,
    maybeShowMealPlanFeaturedHint: _maybeShowMealPlanFeaturedHint,
    maybeShowMealPlanExclusiveHint: _maybeShowMealPlanExclusiveHint,
    maybeShowMealPlanNextDateHint: _maybeShowMealPlanNextDateHint,
    maybeShowMealPlanPrevDateHint: _maybeShowMealPlanPrevDateHint,
    maybeShowMealPlanCreateHint: _maybeShowMealPlanCreateHint,
    maybeShowMealPlanReviewHint: _maybeShowMealPlanReviewHint,
  };
};

export default useMealPlanHint;
